import {
  CreateSurface,
  EntityLaneSurface,
  RequestDetailSurface,
  slugOfCreateSurface,
} from "../../PGuestDetails/layout/createLayoutSurface";

/**
 * What one builder session edits.
 *
 * `detail` is the guest-detail popup — either its unnamed Standard layout or
 * one NAMED entry (`Event.guestDetailLayouts`). That lane is unchanged.
 *
 * `create` is one admin CREATE form (`Event.detailLayouts.<surface>`) — either
 * its unnamed Standard layout or one NAMED version
 * (`detailLayouts.<surface>.layouts`), exactly like the detail lane. No view
 * links though: a saved table view links a POPUP layout
 * (`guestDetailLayoutId` / `detailLayoutId`), never a create form, so the
 * dangling-link sweep stays a no-op for these entities. "Start from an
 * existing layout" remains the copy-from control, which only seeds the DRAFT.
 *
 * `requestDetail` is a request DECISION surface
 * (`Event.detailLayouts.registrantDetail` / `.companionDetail`) — stored like a
 * create lane, resolved with the DETAIL field vocabulary, and falling back to
 * the guest-detail layout until it is configured. Same no-view-links note as
 * the create lanes.
 */
export type LayoutTarget =
  | { kind: "detail"; layoutId?: string }
  | { kind: "create"; surface: CreateSurface; layoutId?: string }
  | { kind: "requestDetail"; surface: RequestDetailSurface; layoutId?: string };

/** Every target that lives in `Event.detailLayouts.<surface>`. */
export type EntityLaneTarget = Extract<
  LayoutTarget,
  { kind: "create" | "requestDetail" }
>;

/** The builder route for a target — also what the lane switcher navigates to. */
export function urlOfLayoutTarget(
  eventId: string,
  target: LayoutTarget,
): string {
  if (target.kind === "create" || target.kind === "requestDetail") {
    // Two route families, ONE slug vocabulary ("registrant"/"companion"): the
    // prefix says which family, so `/…/registrant` cannot mean both.
    const prefix =
      target.kind === "create"
        ? "guest-create-layout-builder"
        : "guest-request-layout-builder";
    const base = `/${prefix}/${eventId}/${slugOfCreateSurface(target.surface)}`;
    return target.layoutId ? `${base}/${target.layoutId}` : base;
  }
  return target.layoutId
    ? `/guest-layout-builder/${eventId}/${target.layoutId}`
    : `/guest-layout-builder/${eventId}`;
}

/** Two things every consumer branches on, named once. */
export function isCreateTarget(
  target: LayoutTarget,
): target is { kind: "create"; surface: CreateSurface; layoutId?: string } {
  return target.kind === "create";
}

/** Stored in `detailLayouts.<surface>` — create lanes AND request lanes. The
 *  builder branches on THIS for everything about storage (document, named
 *  array, persist, CAS); the field vocabulary is a separate axis
 *  (`variantOfSurface`), and conflating the two is what this type prevents. */
export function isEntityLaneTarget(
  target: LayoutTarget,
): target is EntityLaneTarget {
  return target.kind === "create" || target.kind === "requestDetail";
}

/** The lane a target edits, or undefined for the guest-detail layout. */
export function surfaceOfTarget(
  target: LayoutTarget,
): EntityLaneSurface | undefined {
  return isEntityLaneTarget(target) ? target.surface : undefined;
}
